import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";

///fixed classname
export const dropdownTriggerClassname =
  "hover:bg-gray-400 rounded data-[state=open]:border-secondary data-[state=open]:outline-secondary data-[state=open]:border-[1px] border-secondary outline-[0px] focus:outline-[0px] ";

// Define the type for a single menu item in the dropdown
interface MenuItem {
  label: React.ReactNode;
  action: string | (() => void);
  isDisabled?: boolean;
  icon?: string | React.ReactNode;
  isUnPaddedX?: boolean;
  isUnPaddedY?: boolean;
}

interface DropdownProps {
  trigger: React.ReactNode;
  menuItems: MenuItem[];
  projectInfoIcon?: React.ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  className?: string;
  dropDownClassName?: string;
  dropdownContainerClassName?: string;
  addSubContent?: React.ReactNode;
  setOpen?: (open: boolean) => void;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  showTooltipArrowIcon?: boolean; // show tooltip arrow icon
  arrowClassName?: string; // class for arrow UI
  childMenuHover?: boolean; // to keep the child menu open on hover
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  style?: React.CSSProperties;
  onPointerDownOutside?: (event: Event) => void;
  onFocusOutside?: (event: Event) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  menuItems,
  projectInfoIcon,
  align = "start",
  side = "bottom",
  sideOffset = 5,
  className = "",
  dropDownClassName = "",
  dropdownContainerClassName = "",
  addSubContent,
  setOpen,
  open,
  onOpen,
  onClose,
  childMenuHover,
  onScroll,
  style,
  arrowClassName,
  showTooltipArrowIcon,
  onPointerDownOutside,
  onFocusOutside,
}) => {
  const handleMenuItemClick = (action: string | (() => void)) => {
    if (typeof action === "function") {
      action();
    }
  };
  const handleOpenChange = (isOpen: boolean) => {
    setOpen?.(isOpen);
    if (isOpen) {
      onOpen?.();
    } else {
      onClose?.();
    }
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={sideOffset}
          side={side}
          className={`bg-white shadow-lg rounded-md z-50 w-64 border border-gray-100 py-2.5 ${className}`}
          style={style}
          onPointerDownOutside={onPointerDownOutside}
          onFocusOutside={onFocusOutside}
        >
          {showTooltipArrowIcon && (
            <DropdownMenu.Arrow
              className={clsx(
                "fill-white drop-shadow-sm stroke-secondary ",
                arrowClassName,
              )}
              strokeWidth={1}
              width={25}
              height={10}
            />
          )}
          {projectInfoIcon && <>{projectInfoIcon}</>}
          {onScroll && <div onScroll={onScroll}></div>}
          <div
            className={`${dropdownContainerClassName} max-h-[250px]  overflow-auto`}
            onScroll={onScroll}
          >
            {menuItems &&
              menuItems.map((item, index) => (
                <DropdownMenu.Item
                  key={index}
                  onSelect={(e) => {
                    e.stopPropagation();
                    if (item.isDisabled) return;
                    handleMenuItemClick(item.action);
                  }}
                  disabled={item.isDisabled}
                  className={`${dropDownClassName} ${
                    item.isUnPaddedX ? "" : "px-4"
                  }  max-h-[50px] h-[50px] flex w-full items-center gap-3 text-sm text-gray-700 cursor-pointer ${
                    // add height to align all items
                    item.isDisabled
                      ? "cursor-not-allowed text-gray-400"
                      : childMenuHover
                        ? ""
                        : "hover:bg-gray-400 " // update bg color on hover as figma design
                  } outline-none`}
                  // remove py updated figma design
                >
                  {item.icon && (
                    <img
                      src={item.icon as string}
                      alt="icon"
                      className="w-6 h-6 text-gray-500 border border-gray-100 rounded-md"
                    />
                  )}
                  {item.label}
                </DropdownMenu.Item>
              ))}
          </div>
          {addSubContent && <>{addSubContent}</>}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default Dropdown;
